import { useState } from "react";
import { WorldMapPaths } from "./WorldMapPaths";

interface Region {
  id: string;
  name: string;
  city: string;
  country: string;
  x: number; // percentage for positioning
  y: number; // percentage for positioning
  status: "active" | "coming-soon";
  latency?: string;
}

const regions: Region[] = [
  // Europe
  {
    id: "fra",
    name: "HQ (Frankfurt)",
    city: "HQ (Frankfurt)",
    country: "Europe",
    x: 49,
    y: 25,
    status: "active",
    latency: "",
  },

  // Coming Soon
  {
    id: "acc",
    name: "Accra",
    city: "Accra",
    country: "Ghana",
    x: 49.5,
    y: 54.5,
    status: "coming-soon",
  },
  {
    id: "los",
    name: "Lagos",
    city: "Lagos",
    country: "Nigeria",
    x: 51,
    y: 53.5,
    status: "coming-soon",
  },
  {
    id: "kgl",
    name: "Kigali",
    city: "Kigali",
    country: "Rwanda",
    x: 57,
    y: 59,
    status: "coming-soon",
  },
  {
    id: "jnb",
    name: "Johannesburg",
    city: "Johannesburg",
    country: "South Africa",
    x: 56,
    y: 78,
    status: "coming-soon",
  },
  {
    id: "nbo",
    name: "Nairobi",
    city: "Nairobi",
    country: "Kenya",
    x: 59,
    y: 58,
    status: "coming-soon",
  },
];

function GlobalMap() {
  const [hoveredRegion, setHoveredRegion] = useState<string | null>(null);

  const activeRegions = regions.filter((r) => r.status === "active");
  const comingSoonRegions = regions.filter((r) => r.status === "coming-soon");

  return (
    <div className="w-full space-y-8">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-6 border border-gray-200 text-center">
          <div className="text-3xl md:text-4xl font-bold text-vokaris-teal mb-2">
            {activeRegions.length + comingSoonRegions.length}
          </div>
          <div className="text-sm text-vokaris-teal/60">Global Regions</div>
        </div>
        <div className="bg-white rounded-xl p-6 border border-gray-200 text-center">
          <div className="text-3xl md:text-4xl font-bold text-vokaris-teal mb-2">
            99.99%
          </div>
          <div className="text-sm text-vokaris-teal/60">Uptime SLA</div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200 text-center">
          <div className="text-3xl md:text-4xl font-bold text-vokaris-teal mb-2">
            {">"}100
          </div>
          <div className="text-sm text-vokaris-teal/60">Customers building</div>
        </div>
      </div>

      {/* Map */}
      <div className="relative w-full" style={{ aspectRatio: "2000/857" }}>
        <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-vokaris-blue via-white to-vokaris-surface rounded-2xl overflow-hidden border border-gray-200 shadow-lg">
          {/* World Map SVG Background */}
          <svg
            className="absolute inset-0 w-full h-full"
            viewBox="0 0 2000 857"
            preserveAspectRatio="xMidYMid meet"
          >
            <defs>
              <pattern
                id="dot-pattern"
                x="0"
                y="0"
                width="10"
                height="10"
                patternUnits="userSpaceOnUse"
              >
                <circle
                  cx="2.5"
                  cy="2.5"
                  r="2.5"
                  className="text-vokaris-teal/30"
                  fill="currentColor"
                />
              </pattern>
            </defs>
            <g fill="url(#dot-pattern)" stroke="none">
              <WorldMapPaths />
            </g>
          </svg>

          {/* Animated gradient overlay */}
          <div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-vokaris-teal/10 to-transparent animate-shimmer"
            style={{
              backgroundSize: "200% 100%",
              animation: "shimmer 8s infinite linear",
            }}
          />

          {/* Connection Lines */}
          <svg className="absolute inset-0 w-full h-full" style={{ zIndex: 1 }}>
            <defs>
              <linearGradient
                id="lineGradient"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="0%"
              >
                <stop offset="0%" stopColor="rgba(34, 211, 238, 0)" />
                <stop offset="50%" stopColor="rgba(34, 211, 238, 0.4)" />
                <stop offset="100%" stopColor="rgba(34, 211, 238, 0)" />
              </linearGradient>
            </defs>

            {/* Draw curved lines between adjacent regions */}
            {activeRegions.map((region, idx) => {
              if (idx === activeRegions.length - 1) return null;
              const nextRegion = activeRegions[idx + 1];
              const x1 = (region.x / 100) * 100 + "%";
              const y1 = (region.y / 100) * 100 + "%";
              const x2 = (nextRegion.x / 100) * 100 + "%";
              const y2 = (nextRegion.y / 100) * 100 + "%";

              // Calculate control point for curve
              const midX = (region.x + nextRegion.x) / 2;
              const midY = Math.min(region.y, nextRegion.y) - 5;
              const cx = (midX / 100) * 100 + "%";
              const cy = (midY / 100) * 100 + "%";

              return (
                <path
                  key={`connection-${region.id}-${nextRegion.id}`}
                  d={`M ${x1} ${y1} Q ${cx} ${cy} ${x2} ${y2}`}
                  stroke="url(#lineGradient)"
                  strokeWidth="1.5"
                  fill="none"
                  strokeDasharray="5,5"
                  className="animate-pulse"
                  opacity="0.6"
                />
              );
            })}
          </svg>
        </div>

        {/* Region Markers */}
        {regions.map((region) => {
          const isHovered = hoveredRegion === region.id;
          const isActive = region.status === "active";

          return (
            <div
              key={region.id}
              className="absolute transform -translate-x-1/2 -translate-y-1/2 group"
              style={{
                left: `${region.x}%`,
                top: `${region.y}%`,
                zIndex: isHovered ? 20 : 10,
              }}
              onMouseEnter={() => setHoveredRegion(region.id)}
              onMouseLeave={() => setHoveredRegion(null)}
            >
              {/* Pulse ring for active regions */}
              {isActive && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-6 h-6 md:w-12 md:h-12 rounded-full bg-vokaris-green/20 animate-ping" />
                </div>
              )}

              {/* Main dot */}
              <div
                className={`relative w-2 h-2 md:w-4 md:h-4 rounded-full cursor-pointer transition-all duration-300 ${
                  isActive
                    ? "bg-vokaris-green shadow-lg shadow-vokaris-green/50"
                    : "bg-gray-400"
                } ${isHovered ? "scale-150" : "scale-100"}`}
              >
                {/* Inner glow */}
                <div className="absolute inset-0 rounded-full bg-white/40 animate-pulse" />
              </div>

              {/* Label */}
              <div
                className={`absolute left-1/2 -translate-x-1/2 mt-3 whitespace-nowrap transition-all duration-300 pointer-events-none ${
                  isHovered
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-2"
                }`}
              >
                <div className="bg-white px-4 py-3 rounded-xl shadow-xl border border-gray-200">
                  <div className="font-bold text-vokaris-teal text-sm">
                    {region.city}
                  </div>
                  <div className="text-xs text-vokaris-teal/60">
                    {region.country}
                  </div>
                  {isActive && region.latency && (
                    <div className="text-xs text-vokaris-green font-semibold mt-1">
                      {region.latency} latency
                    </div>
                  )}
                  {!isActive && (
                    <div className="text-xs text-gray-400 mt-1">
                      Coming Soon
                    </div>
                  )}
                </div>
                {/* Arrow pointing to dot */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-white border-l border-t border-gray-200 transform rotate-45" />
              </div>
            </div>
          );
        })}
      </div>

      {/* Region List */}
      <div className="grid md:grid-cols-3 gap-4">
        {activeRegions.map((region) => (
          <div
            key={region.id}
            onMouseEnter={() => setHoveredRegion(region.id)}
            onMouseLeave={() => setHoveredRegion(null)}
            className={`bg-white rounded-xl p-4 border transition-all duration-300 cursor-pointer ${
              hoveredRegion === region.id
                ? "border-vokaris-green shadow-lg shadow-vokaris-green/10 scale-105"
                : "border-gray-200 hover:border-vokaris-teal/30"
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="font-bold text-vokaris-teal">{region.city}</div>
                <div className="text-sm text-vokaris-teal/60">
                  {region.country}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-vokaris-green animate-pulse" />
                <span className="text-xs text-vokaris-teal/60">
                  {region.latency}
                </span>
              </div>
            </div>
          </div>
        ))}

        {comingSoonRegions.map((region) => (
          <div
            key={region.id}
            className="bg-gray-50 rounded-xl p-4 border border-gray-200 opacity-60"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="font-bold text-vokaris-teal">{region.city}</div>
                <div className="text-sm text-vokaris-teal/60">
                  {region.country}
                </div>
              </div>
              <span className="text-xs text-gray-400">Planned</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default GlobalMap;
